import axios from "axios";
import { getCaptchaToken } from "./captcha";

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";

// Endpoints the backend gates with CaptchaRequired. Keep in sync with
// backend/api/permissions.py usages.
const CAPTCHA_ENDPOINTS = [
  "sales/contact/",
  "sales/email/",
  "marketplace/contact/send/",
  "send_email/",
  "send_eureka_bill/",
  "register/",
  "correct_test_no_student/",
  "correct_tests_report/",
  // Student entry point: one lookup per session, so a challenge here behaves
  // like a login captcha. Query string is stripped before matching.
  "schools/check-test-rut/"
];

function needsCaptcha(config) {
  // Exact match: "register/" must not catch "easy-register/".
  const url = (config.url || "").split("?")[0].replace(/^\//, "");
  if (CAPTCHA_ENDPOINTS.includes(url)) return true;
  // Link viewsets: only send-link, which actually sends mail. create and the
  // report actions are rate-limited server-side instead, so they stay fast.
  return /^(material|test)-links\/[^/]+\/send-link\/?$/.test(url);
}

const instance = axios.create({
  baseURL: "/api",
  timeout: 50000000,
  headers: {
    "Content-Type": "application/json",
    Authorization: {
      toString() {
        return `Token ${localStorage.getItem("token")}`;
      }
    }
  }
});

instance.interceptors.request.use(async (config) => {
  // Logged-in callers pass server-side without a captcha, so skip the
  // challenge and keep their requests fast. localStorage is only the
  // frontend's idea of a session though — if it has gone stale the backend
  // answers 403 and the response interceptor below retries with a token.
  // __captchaRetried: the replay below already attached a fresh token.
  if (config.__captchaRetried) return config;
  if (!needsCaptcha(config) || localStorage.getItem("token")) return config;
  try {
    config.headers["X-Captcha-Token"] = await getCaptchaToken();
  } catch (e) {
    // Script blocked or challenge failed. Send it anyway and let the backend
    // decide — it may not have needed the captcha at all.
    console.warn("captcha:", e.message);
  }
  return config;
});

// A 403 on a gated endpoint means the captcha was required after all (stale
// session). Solve one and replay the request once, so the desync is invisible.
instance.interceptors.response.use(undefined, async (error) => {
  const { config, response } = error;
  if (!config || !response || response.status !== 403) throw error;
  if (config.__captchaRetried || !needsCaptcha(config)) throw error;
  config.__captchaRetried = true;
  try {
    config.headers["X-Captcha-Token"] = await getCaptchaToken();
  } catch (e) {
    throw error; // surface the original 403, not the captcha failure
  }
  return instance(config);
});

export default instance;
